import { Trans, useLingui } from '@lingui/react';
import { Box, Theme } from '@mui/material';
import { I18n, i18n as i18nCore } from '@lingui/core';
import React, { ReactNode } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { containsHtml } from '../String';

export declare type Values = Record<string, unknown>;
declare type Components = Record<string, JSX.Element>;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& a': {
      color: theme.palette.secondary.main,
    },
  },
}));

export const getFormattedTranslation = (
  id: string,
  translation: string,
  displayHtml = false
) => {
  // if id is the same as translation, it means there is not translation
  if (id === translation) {
    return '';
  }

  if (displayHtml && containsHtml(translation)) {
    return (
      <Box display="inline" dangerouslySetInnerHTML={{ __html: translation }} />
    );
  }

  return translation;
};

export const translationByLang = (
  id: string,
  language: string,
  values?: Values | undefined
) => {
  const currentMessages = { ...(i18nCore as any)._messages };

  if (!currentMessages[language]) {
    return '';
  }

  // let the lingui core make the interpolation
  const translation = i18nCore._({
    message: currentMessages[language][id],
    values,
  });

  return getFormattedTranslation(id, translation);
};

export type T = (
  id: string,
  values?: Values | undefined,
  i18nService?: I18n
) => string;

// call this method when we need the translation outside of the react component
export const t: T = (
  id: string,
  values?: Values | undefined,
  i18nService = i18nCore
): string => {
  const translation = i18nService._(id, values);

  return getFormattedTranslation(id, translation).toString();
};

export const useI18n = () => {
  // we need to use this hook to have the latest updates when the language changes
  const { i18n } = useLingui();
  const i18nClasses = useStyles();

  // use this method only for string translations
  const translation = (id: string, values?: Values | undefined): string =>
    t(id, values, i18n);

  // use this method only for translations with html
  const tHtml = (
    id: string,
    values?: Values | undefined
  ): string | JSX.Element => {
    const translation = i18n._(id, values);
    return getFormattedTranslation(id, translation, true);
  };

  // use this method only for translations with components
  const tc = (
    id: string,
    values?: Values | undefined,
    components?: Components,
    translateHtml = false
  ): string | JSX.Element => (
    <Trans
      id={id}
      values={values}
      components={components}
      render={({ translation }) => {
        // use this param when we need to add a component and translate string to html
        if (translateHtml) {
          const translationFormatted = (
            typeof translation === 'string' ? [translation] : translation
          ) as ReactNode[];
          return translationFormatted.map(
            (data: string | React.ReactNode, index: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={`${id}-${index}`}>
                {typeof data === 'string'
                  ? getFormattedTranslation(id, data, true)
                  : data}
              </React.Fragment>
            )
          ) as any;
        }

        return translation;
      }}
    />
  );

  return { i18n, i18nClasses, t: translation, tc, tHtml };
};
